import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import React from 'react';
import { TrackedSelect, TrackedTextField } from 'src/theme/StyledFields';
import { resolveQuestionLabel } from '../../hooks/useIntakeSurvey';
import { IntakeAnswers, IntakeQuestion } from '../../types/intakeSurvey';
import OrganizationDirectory from '../OrganizationDirectory';
import { getIntakeIcon } from './intakeSectionIcons';

interface IntakeSurveyFormProps {
  question: IntakeQuestion;
  answers: IntakeAnswers;
  fieldError?: string;
  onChange: (id: string, value: string | number | string[]) => void;
}

const IntakeSurveyForm: React.FC<IntakeSurveyFormProps> = ({
  question,
  answers,
  fieldError,
  onChange,
}) => {
  const label = resolveQuestionLabel(question, answers);
  const value = answers[question.id];
  const QuestionIcon = getIntakeIcon(question.icon);

  const labelWithIcon = QuestionIcon ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <QuestionIcon fontSize="small" color="action" aria-hidden />
      <span>{label}</span>
    </Box>
  ) : (
    label
  );

  const renderField = () => {
    switch (question.type) {
      case 'organization_select':
        return (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {labelWithIcon}
            </Typography>
            <OrganizationDirectory
              value={value as number | string | undefined}
              onChange={(orgId) => onChange(question.id, orgId)}
              error={fieldError}
            />
          </Box>
        );
      case 'short_text':
      case 'phone':
      case 'zip':
        return (
          <TrackedTextField
            fullWidth
            label={labelWithIcon}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(question.id, e.target.value)}
            error={!!fieldError}
            helperText={fieldError}
          />
        );
      case 'email':
        return (
          <TrackedTextField
            fullWidth
            type="email"
            label={labelWithIcon}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(question.id, e.target.value)}
            error={!!fieldError}
            helperText={fieldError}
          />
        );
      case 'number':
        return (
          <TrackedTextField
            fullWidth
            type="number"
            label={labelWithIcon}
            value={value === undefined || value === null ? '' : String(value)}
            onChange={(e) => onChange(question.id, e.target.value === '' ? '' : parseInt(e.target.value, 10))}
            error={!!fieldError}
            helperText={fieldError}
            inputProps={{ min: 1, max: 120 }}
          />
        );
      case 'single_select':
      case 'range_select':
      case 'yes_no_not_sure':
        return (
          <FormControl fullWidth error={!!fieldError}>
            <InputLabel id={`${question.id}-label`}>{label}</InputLabel>
            <TrackedSelect
              labelId={`${question.id}-label`}
              label={label}
              value={(value as string) ?? ''}
              onChange={(e) => onChange(question.id, e.target.value)}
            >
              {(question.choices ?? []).map((choice) => (
                <MenuItem key={choice.value} value={choice.value}>
                  {choice.label}
                </MenuItem>
              ))}
            </TrackedSelect>
            {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
          </FormControl>
        );
      case 'multi_select': {
        const selected = Array.isArray(value) ? value : [];
        return (
          <FormControl fullWidth error={!!fieldError}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {labelWithIcon}
            </Typography>
            <TrackedSelect
              multiple
              value={selected}
              onChange={(e) => {
                const v = e.target.value;
                onChange(question.id, typeof v === 'string' ? v.split(',') : v);
              }}
              renderValue={(selectedValues) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selectedValues as string[]).map((val) => {
                    const choice = question.choices?.find((c) => c.value === val);
                    return <Chip key={val} label={choice?.label ?? val} size="small" />;
                  })}
                </Box>
              )}
            >
              {(question.choices ?? []).map((choice) => (
                <MenuItem key={choice.value} value={choice.value}>
                  <Checkbox checked={selected.includes(choice.value)} />
                  <ListItemText primary={choice.label} />
                </MenuItem>
              ))}
            </TrackedSelect>
            {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
          </FormControl>
        );
      }
      default:
        return null;
    }
  };

  return <Box sx={{ mb: 3 }}>{renderField()}</Box>;
};

export default IntakeSurveyForm;
